import React, { useEffect, useState, useContext, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { SimpleTooltip } from "simple-ui-components";

import ChromeReaderModeIcon from "@mui/icons-material/ChromeReaderMode";
import LocationSearchingIcon from "@mui/icons-material/LocationSearching";
import RoomIcon from "@mui/icons-material/Room";

import {
  BodyWrapperStyled,
  BoxStyled,
  CardStyled,
  CardHeader,
  CardBody,
  OuterWrapper,
  CardFilter,
  EmptyList,
} from "../../theme";
import { Layout, useWindowSize } from "../layout/otherPage";
import { get } from "../../server-requests/serverRequests";
import { LoadingList } from "../shared/loading";
import { RoutesDetail } from "../routes/routesDetail";
import { AuthContext } from "../authContextProvider";
import { SelectedEquipment } from "./selectedEquipment";
import { PointDetail } from "./pointDetail";
import { FilterRoutes } from "./filterRoutes";
import { getTranslation, LanguageContext } from "../languageProvider";
import { Log, ReplaceWord } from "../shared/utility";

const filterFinishedRouteList = (list) => {
  let listToReturn = [];
  for (let route of list) {
    if (route.started && route.ended) {
      listToReturn = [...listToReturn, route];
    }
  }
  let toReturn = listToReturn.sort(
    (a, b) => new Date(b.ended) - new Date(a.ended)
  );
  return toReturn;
};

const Reports = () => {
  const { Tooltip } = SimpleTooltip;
  const navigate = useNavigate();

  const { snackbarHandler } = useContext(AuthContext);
  const { lang } = useContext(LanguageContext);

  const { wrapperHeight, boxHeight } = useWindowSize();

  const [selectedRoutesList, setSelectedRoutesList] = useState(new Map());
  const [reloadRoutes, setReloadRoutes] = useState(true);
  const [routesList, setRoutesList] = useState([]);
  const [routesListFull, setRoutesListFull] = useState([]);
  const [loadingRoutes, setLoadingRoutes] = useState(true);

  const [selectedRoute, setSelectedRoute] = useState("");

  const [filter, setFilter] = useState([]);
  const [filterRoute, setFilterRoute] = useState(false);

  const [reloadPoints, setReloadPoints] = useState(false);
  const [filterPoints, setFilerPoints] = useState(false);
  const [pointsList, setPointsList] = useState([]);
  const [filteredPointsList, setFilteredPointsList] = useState([]);

  const [selectedPointList, setSelectedPointList] = useState([]);
  const [reloadSelectedPointList, setReloadSelectedPoint] = useState(false);

  const [reRender, setReRender] = useState(false);

  // get Points
  useEffect(() => {
    let isMounted = true;
    if (reloadPoints) {
      get("routes/points/list", {
        routeId: selectedRoute && selectedRoute.id,
      })
        .then((points) => {
          if (isMounted) {
            setPointsList(points);
            setFilerPoints(true);
            setReloadPoints(false);
          }
        })
        .catch((error) => {
          setReloadPoints(false);
          Log("Error: get routes/technicians/list" + error);
        });
    }
    return () => (isMounted = false);
  }, [reloadPoints, selectedRoute]);

  // filter points
  useEffect(() => {
    if (filterPoints) {
      let newList = pointsList;
      if (selectedPointList.length > 0 && pointsList.length > 0) {
        let isExist = selectedPointList.filter(
          (item) => item.route.id === selectedRoute.id
        );
        if (isExist.length > 0) {
          for (let selectedPoint of isExist[0].points) {
            for (let point of pointsList) {
              if (point.id === selectedPoint.id)
                newList = newList.filter((item) => item.id !== point.id);
            }
          }
        }
      }
      setFilteredPointsList(newList);
      setFilerPoints(false);
    }
  }, [pointsList, selectedPointList, filterPoints, selectedRoute]);

  // get Routes List
  useEffect(() => {
    let isMounted = true;
    if (reloadRoutes) {
      get("routes/list")
        .then((routes) => {
          if (isMounted) {
            let list = filterFinishedRouteList(routes);
            setRoutesList(list);
            setRoutesListFull(list);

            // selected Routes
            if (list.length > 0) {
              setSelectedRoute(list[0]);
              setReloadPoints(true);
            }
            setLoadingRoutes(false);
            setReloadRoutes(false);
          }
        })
        .catch((error) => {
          setLoadingRoutes(false);
          setReloadRoutes(false);
          Log("Error: get routes/list" + error);
        });
    }
    return () => (isMounted = false);
  }, [reloadRoutes]);

  const selectRouteClickHandler = useCallback((route) => {
    setSelectedRoute(route);
    setReloadPoints(true);
  }, []);

  const addPointToReport = (route, equipment) => {
    let selectedRoute = selectedRoutesList.get(route.id);
    if (selectedRoute) {
      let selectedPoint = selectedRoute.route.points.get(equipment.id);
      if (!selectedPoint) {
        if (equipment.started) {
          selectedRoute.route.points.set(equipment.id, { ...equipment });
        } else {
          snackbarHandler(getTranslation("0094"), "warning");
        }
      } else {
        snackbarHandler(getTranslation("0093"), "warning");
      }
    } else {
      if (equipment.started) {
        let points = new Map();
        points.set(equipment.id, {
          ...equipment,
        });
        selectedRoutesList.set(route.id, {
          route: {
            id: route.id,
            name: route.name,
            points,
          },
        });
      } else {
        snackbarHandler(getTranslation("0094"), "info");
      }
    }
    setSelectedRoutesList(selectedRoutesList);
    setReloadSelectedPoint(true);
    setReRender((prev) => !prev);
  };

  const removePointFromReport = (route, equipment) => {
    let selectedRoute = selectedRoutesList.get(route.id);
    if (selectedRoute) {
      let selectedPoint = selectedRoute.route.points.get(equipment.id);
      if (selectedPoint) {
        selectedRoute.route.points.delete(equipment.id);
      }
      if (selectedRoute.route.points.size === 0) {
        selectedRoutesList.delete(route.id);
      }
    }
    setSelectedRoutesList(selectedRoutesList);
    setReloadSelectedPoint(true);
    setReRender((prev) => !prev);
  };

  // get Selected Point list
  useEffect(() => {
    if (reloadSelectedPointList) {
      let formattedList = [];
      selectedRoutesList.forEach((routeItem) => {
        let points = [];
        routeItem.route.points.forEach((pointItem) => {
          points.push(pointItem);
        });
        formattedList.push({
          route: routeItem.route,
          points,
        });
      });
      setSelectedPointList(formattedList);
      setFilerPoints(true);
      setReloadSelectedPoint(false);
    }
  }, [selectedRoutesList, reloadSelectedPointList]);

  const generateReportClickHandler = () => {
    let list = selectedPointList;
    let listToReturn = [];
    for (let route of list) {
      let points = [];
      for (let point of route.points) {
        points = [...points, point];
      }
      listToReturn = [...listToReturn, { id: route.route.id, points }];
    }
    if (listToReturn.length > 0) {
      // set list
      localStorage.setItem("generateReport", JSON.stringify(listToReturn));
      //open generate report page
      navigate("generateReport");
    }
  };

  const filterRouteSearchHandler = (value) => {
    setFilter(value);
    setFilterRoute(true);
  };

  // Filter List
  useEffect(() => {
    if (filterRoute) {
      let list = routesListFull;
      if (filter) {
        if (filter !== "") {
          list = list.filter((route) => {
            let routeName = route.name ? route.name.toUpperCase() : "";
            let items = routeName.includes(filter.toUpperCase());
            if (!items && route.points.length > 0) {
              let list = route.points.filter((item) => {
                let serialNumber = item.equipment.serialNumber.toUpperCase();
                return serialNumber.includes(filter.toUpperCase());
              });
              if (list.length > 0) return true;
            }
            return items;
          });
        }
      }
      setRoutesList(list);
      setFilterRoute(false);
    }
  }, [routesListFull, filterRoute, filter]);
  return (
    <Layout lang={lang}>
      <OuterWrapper height={wrapperHeight}>
        <BodyWrapperStyled fullHeight>
          <BoxStyled width="sm" col="3" noMargin>
            <CardStyled>
              <CardHeader>
                <LocationSearchingIcon className="mainIcon" />
                <div className="detail" data-translate="0008">
                  {getTranslation("0008")}s
                  <span data-translate="0008,0078">
                    {ReplaceWord(
                      getTranslation("0078"),
                      "**NUMBER**",
                      routesList.length,
                      "**LISTNAME**",
                      getTranslation("0008").toLowerCase() + "s"
                    )}
                  </span>
                </div>
              </CardHeader>
              <CardFilter>
                <FilterRoutes Filter={filterRouteSearchHandler} />
              </CardFilter>
              <CardBody height={boxHeight} filter={64}>
                {loadingRoutes && <LoadingList count={5} />}
                {!loadingRoutes && routesList.length === 0 && (
                  <EmptyList data-translate="0008,0123">
                    {ReplaceWord(
                      getTranslation("0123"),
                      "**NAME**",
                      getTranslation("0008").toLowerCase()
                    )}
                  </EmptyList>
                )}
                {!loadingRoutes &&
                  routesList.length > 0 &&
                  routesList.map((route, index) => (
                    <RoutesDetail
                      key={index}
                      route={route}
                      Select={selectRouteClickHandler}
                      isActive={selectedRoute.id === route.id}
                    />
                  ))}
              </CardBody>
            </CardStyled>
          </BoxStyled>
          <BoxStyled width="sm" col="3" noMargin>
            <CardStyled>
              <CardHeader>
                <RoomIcon className="mainIcon" />
                <div className="detail" data-translate="0079">
                  {getTranslation("0079")}
                  {selectedRoute && (
                    <span data-translate="0080,0081">
                      {ReplaceWord(
                        getTranslation("0080"),
                        "**COUNT**",
                        pointsList.length,
                        "**NAME1**",
                        getTranslation("0081").toLowerCase(),
                        "**NAME2**",
                        selectedRoute && selectedRoute.name
                      )}
                    </span>
                  )}
                </div>
              </CardHeader>
              <CardBody height={boxHeight}>
                {loadingRoutes && <LoadingList count={5} />}
                {!loadingRoutes && filteredPointsList.length === 0 && (
                  <EmptyList data-translate="0079,0123">
                    {ReplaceWord(
                      getTranslation("0123"),
                      "**NAME**",
                      getTranslation("0079").toLowerCase()
                    )}
                  </EmptyList>
                )}
                {!loadingRoutes &&
                  filteredPointsList &&
                  filteredPointsList.map((point, index) => (
                    <PointDetail
                      key={index}
                      selectedRoute={selectedRoute}
                      point={point}
                      Add={addPointToReport}
                    />
                  ))}
              </CardBody>
            </CardStyled>
          </BoxStyled>
          <BoxStyled width="sm" col="3" noMargin>
            <CardStyled>
              <CardHeader>
                <ChromeReaderModeIcon className="mainIcon" />
                <div className="detail" data-translate="0075,0090">
                  {getTranslation("0075")}
                  <span>{getTranslation("0090")}</span>
                </div>
                {selectedPointList.length > 0 && (
                  <Tooltip text={getTranslation("0091")} placement="left">
                    <ChromeReaderModeIcon
                      onClick={generateReportClickHandler}
                    />
                  </Tooltip>
                )}
              </CardHeader>
              <CardBody height={boxHeight}>
                {selectedRoutesList.size === 0 && (
                  <EmptyList data-translate="0079,0123">
                    {ReplaceWord(
                      getTranslation("0123"),
                      "**NAME**",
                      getTranslation("0079").toLowerCase()
                    )}
                  </EmptyList>
                )}
                {selectedRoutesList.size > 0 &&
                  selectedPointList.map((item, index) => (
                    <SelectedEquipment
                      key={index}
                      item={item}
                      reRender={reRender}
                      Remove={removePointFromReport}
                    />
                  ))}
              </CardBody>
            </CardStyled>
          </BoxStyled>
        </BodyWrapperStyled>
      </OuterWrapper>
    </Layout>
  );
};
export default Reports;
